import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {ActionOrigin} from 'constants/AnalyticsConstants';
import {StudyDesignType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import {FrozenEntityChipData, PlateRecord} from 'samples/plate-maps/plate-record/PlateRecordConstants';
import {
  getDisplayLayerDropdownOptionsForDataParameterValues,
  getDisplayLayerDropdownOptionsForFrozenValues,
  getFrozenEntityChipData,
  getHydratedPlateRecordData,
  getPlateRecordAsPlateMap,
  getReagentAssignmentGroupsFromWells,
} from 'samples/plate-maps/plate-record/PlateRecordUtil';
import {
  PlateMaterialInput,
  PlateMap,
  ReagentAssignmentGroup,
  SchematizedAttributeType,
  SUPPORTED_FIELD_TYPES_FOR_ANNOTATION,
  SUPPORTED_FIELD_TYPES_FOR_DATA_PARAMETERS,
} from 'samples/plate-maps/PlateMapConstants';
import PlateMapSidebarMetadata from 'samples/plate-maps/PlateMapSidebarMetadata';
import PlateMapSummary from 'samples/plate-maps/PlateMapSummary';
import getTestProps from 'test/lib/cypress/getTestProps';
import useOnMount from 'ui-library/hooks/useOnMount';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

export interface PlateRecordSummaryProps {
  plateRecord: PlateRecord;
  plateMaterialInputs?: Array<PlateMaterialInput>;
  /* Design type of the associated procedure's study. This controls showing test order/dilution related
  information only for sample testing studies. */
  procedureStudyDesignType: StudyDesignType | null;
  analyticsMetadata: {
    plateId: string;
    transferRecordId: string;
    fileId: string;
    actionOrigin: ActionOrigin;
  };
}

/**
 * A summary of a plate record that is shown in Notebook entries and documents.
 */
export default function PlateRecordSummary({
  plateRecord,
  plateMaterialInputs,
  procedureStudyDesignType,
  analyticsMetadata,
}: PlateRecordSummaryProps): JSX.Element {
  const [plateMap, setPlateMap] = useState<PlateMap | null>(null);
  const [frozenEntityChipData, setFrozenEntityChipData] = useState<{
    [id: string]: FrozenEntityChipData;
  }>({});
  const [frozenReagentAssignmentGroups, setFrozenReagentAssignmentGroups] = useState<{
    [id: string]: ReagentAssignmentGroup;
  } | null>(null);

  useOnMount(() => {
    const fetchAndSetPlateMap = async (): Promise<void> => {
      const {liveEntityChipData, schemaIdsByReagentId, unitsById, testOrderInfoById} =
        await getHydratedPlateRecordData(plateRecord);
      const plateMap = getPlateRecordAsPlateMap(
        plateRecord,
        schemaIdsByReagentId,
        unitsById,
        testOrderInfoById
      );
      const frozenReagentAssignmentGroups = getReagentAssignmentGroupsFromWells(
        plateRecord.wellTransferRecords,
        'transferRecord'
      );
      const entityChipData = getFrozenEntityChipData(
        plateRecord.wellTransferRecords.map(
          (plateWellTransferRecord) => plateWellTransferRecord.transferRecord
        ),
        liveEntityChipData
      );
      setFrozenReagentAssignmentGroups(frozenReagentAssignmentGroups);
      setPlateMap(plateMap);
      setFrozenEntityChipData(entityChipData);
    };
    fetchAndSetPlateMap();
  });

  const wellSchemaOptions = getDisplayLayerDropdownOptionsForFrozenValues(
    plateRecord.wellTransferRecords.flatMap((wellTransferRecord) => wellTransferRecord.schemaFieldValues),
    SchematizedAttributeType.WELL_SCHEMA_FIELD,
    SUPPORTED_FIELD_TYPES_FOR_ANNOTATION
  );

  const plateDataParametersOptions = getDisplayLayerDropdownOptionsForDataParameterValues(
    plateRecord.wellTransferRecords.flatMap(
      (wellTransferRecord) => wellTransferRecord.dataParameterValues ?? []
    ),
    SchematizedAttributeType.DATA_PARAMETER,
    SUPPORTED_FIELD_TYPES_FOR_DATA_PARAMETERS
  );

  return (
    <div
      className={css(styles.contents)}
      {...getTestProps({component: 'PlateRecordSummary', element: 'root'})}
    >
      {plateMap != null ? (
        <>
          <PlateMapSummary
            plateRecord={plateRecord}
            plateMap={plateMap}
            plateDataParametersOptions={plateDataParametersOptions}
            wellSchemaOptions={wellSchemaOptions}
            frozenReagentAssignmentGroups={frozenReagentAssignmentGroups}
            procedureStudyDesignType={procedureStudyDesignType}
            plateMapType={PlateMapType.RECORD}
            additionalMetadataContent={
              <PlateMapSidebarMetadata
                metadata={[]}
                plateRecordDetailModalProps={{
                  plateMapType: PlateMapType.RECORD,
                  plateMap,
                  plateDataParametersOptions,
                  plateMaterialInputs,
                  wellSchemaOptions,
                  frozenReagentAssignmentGroups,
                  frozenEntityChipData,
                  procedureStudyDesignType,
                  onRequestClose: noop,
                }}
                viewDetailsAnalyticsMetadata={analyticsMetadata}
              />
            }
          />
        </>
      ) : (
        <CenteredHorizontalThrobber />
      )}
    </div>
  );
}
const styles = StyleSheet.create({
  contents: {
    display: 'flex',
    width: '100%',
  },
  metadataChip: {
    marginBottom: '4px',
  },
});
